import { Alert, Button, Icon, Input, ProgressSpinner } from '@dittolive/anvil'
import { CheckCircleIcon } from '@phosphor-icons/react'
import { graphql } from 'babel-plugin-relay/macro'
import debounce from 'lodash/debounce'
import React, { useCallback, useRef, useState } from 'react'
import { fetchQuery } from 'react-relay'
import { useRelayEnvironment } from 'react-relay/hooks'
import { usePortalMutation } from 'utils'

import { AppOrganizationSlugDestinationInputInputQuery } from './__generated__/AppOrganizationSlugDestinationInputInputQuery.graphql'
import { AppOrganizationSlugDestinationInputTransferMutation } from './__generated__/AppOrganizationSlugDestinationInputTransferMutation.graphql'
import TransferAppConfirmationModal from './TransferAppConfirmationModal'

const query = graphql`
  query AppOrganizationSlugDestinationInputInputQuery($slug: String!) {
    viewer {
      organization(slug: $slug) {
        id
      }
    }
  }
`

const transferMutation = graphql`
  mutation AppOrganizationSlugDestinationInputTransferMutation(
    $appId: ID!
    $organizationId: ID!
  ) {
    requestAppTransferToOrganization(
      appId: $appId
      organizationId: $organizationId
    ) {
      transferRequestDestination {
        ... on Organization {
          name
          imageUrl
        }
      }
    }
  }
`

type Props = {
  appId: string
}

const AppOrganizationSlugDestinationInput: React.FC<Props> = ({ appId }) => {
  const environment = useRelayEnvironment()

  const [transferApp, isTransferringApp] =
    usePortalMutation<AppOrganizationSlugDestinationInputTransferMutation>(
      transferMutation,
      {
        mutationName: 'RequestAppTransferToOrganization',
        sentryContext: {
          component: 'AppOrganizationSlugDestinationInput',
        },
        showErrorToast: false,
      },
    )
  const [value, setValue] = useState('')
  const [destinationOrganizationId, setDestinationOrganizationId] =
    useState<string>()
  const [isVerifyingSlug, setIsVerifyingSlug] = useState(false)
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const [transferError, setTransferError] = useState<Error>()
  const queryIdRef = useRef(0)
  const debouncedOrganizationCheck = useRef(
    debounce((search: string, queryId: number) => {
      fetchQuery<AppOrganizationSlugDestinationInputInputQuery>(
        environment,
        query,
        { slug: search },
        { networkCacheConfig: { force: true } },
      )
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        .toPromise()
        .then((result) => {
          if (queryIdRef.current !== queryId) return
          setDestinationOrganizationId(result?.viewer.organization?.id)
          setIsVerifyingSlug(false)
        })
        .catch(() => {
          if (queryIdRef.current !== queryId) return
          setIsVerifyingSlug(false)
        })
      // Can't use finally here to call setIsVerifyingSlug(false) since there's some incompatibility issues with react-testing-library
    }, 1000),
  )

  const handleSlugChanged = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      setDestinationOrganizationId(undefined)
      setValue(evt.target.value)
      if (evt.target.value) {
        queryIdRef.current += 1
        const nextQueryId = queryIdRef.current
        setIsVerifyingSlug(true)
        debouncedOrganizationCheck.current(evt.target.value, nextQueryId)
      }
    },
    [],
  )

  const handleTransferClicked = () => {
    setTransferError(undefined)
    setIsConfirmationOpen(true)
  }

  const handleConfirmationClosed = (confirmTransfer: boolean) => {
    if (confirmTransfer && destinationOrganizationId) {
      transferApp({
        variables: { appId, organizationId: destinationOrganizationId },
        onCompleted: () => {
          setIsConfirmationOpen(false)
        },
        onError: (err) => {
          setTransferError(err)
        },
      })
    } else {
      setIsConfirmationOpen(false)
    }
  }

  const isInvalid = !isVerifyingSlug && !!value && !destinationOrganizationId

  return (
    <>
      {!!transferError && (
        <Alert
          autoDismiss={false}
          variant="danger"
        >{`Could not create the database transfer request: ${transferError.message}`}</Alert>
      )}
      <TransferAppConfirmationModal
        isOpen={isConfirmationOpen}
        onClose={handleConfirmationClosed}
        appId={appId}
        destination={value}
        isProcessing={isTransferringApp}
      />
      <div className="w-full">
        <Input
          autoComplete="off"
          trailingIcon={
            destinationOrganizationId ? (
              <Icon
                svg={<CheckCircleIcon weight="fill" />}
                data-testid="verifiedCheckmark"
                className="text-fill-success"
              />
            ) : isVerifyingSlug ? (
              <ProgressSpinner
                data-testid="verifyingOrganization"
                className="text-foreground-normal size-4"
                progressColor="currentColor"
              />
            ) : null
          }
          id="organizationSlug"
          label="Organization slug"
          description="The slug of the organization in the portal"
          onChange={handleSlugChanged}
          placeholder="portal-slug-for-organization"
          value={value}
          errorMessage={
            isInvalid
              ? 'No organization was found with the provided slug'
              : undefined
          }
        />
      </div>
      <Button
        disabled={!destinationOrganizationId}
        onClick={handleTransferClicked}
        className="mt-8"
        variant="critical"
      >
        Transfer
      </Button>
    </>
  )
}

export default AppOrganizationSlugDestinationInput
